/**
 * The last leaderboard, concept lists and concepts, kept in this browser between visits, so a reload or a new
 * tab draws them at once and refreshes them behind, instead of showing skeletons until every read is back.
 *
 * Kept narrow on purpose:
 *
 * - only small, everyday reads (`PERSISTED`): the leaderboard and its cards, the periods, the concept lists, the
 *   lookups and single concepts. Never a storyboard or an editor session (hundreds of KB each), never the
 *   session itself, never anything an admin page reads about other people;
 * - one person's: the store names whoever was signed in (and whom they were viewing as). It is restored only
 *   after the session has answered, and only to that same person;
 * - one build's, and short-lived: a deploy or six hours away starts afresh;
 * - gone on sign-out, a switch of person and "view as" (`use-session.tsx`).
 *
 * Restored reads keep their age, so every page still refreshes them as it opens. Signed Cloudflare copies inside
 * them are ignored once expired (`lib/preview.ts`). Storage that is blocked or full simply means no restore.
 */
import { dehydrate, hydrate, type DehydratedState, type QueryClient } from "@tanstack/react-query";
import type { SessionInfo } from "@scandi/shared";

const STORAGE_KEY = "scandi.query-cache";
const VERSION = 1;
const MAX_AGE_MS = 6 * 60 * 60 * 1000;
/** Above this the store is not written: a few lists, not a library. */
const MAX_BYTES = 1_500_000;

/** The reads worth keeping, by the first parts of their keys. */
function persisted(key: readonly unknown[]): boolean {
  const [a, b] = key;
  if (a === "leaderboard" || a === "periods" || a === "new-winners" || a === "prize-contests" || a === "ai-concepts" || a === "strategist") return true;
  if (a === "pipeline") return b === "briefs" || b === "brief" || b === "lookups";
  return false;
}

interface Stored {
  version: number;
  build: string;
  owner: string;
  savedAt: number;
  state: DehydratedState;
}

/** Whose reads these are: the account (or team login), its role, and whom it is viewing as. */
function ownerOf(session: SessionInfo | null | undefined): string | null {
  if (!session?.authenticated || !session.role) return null;
  return JSON.stringify([session.user?.id ?? null, session.realRole ?? session.role, session.role, session.viewAs ?? null]);
}

function read(): Stored | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
}

export function clearPersistedQueries(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing kept.
  }
}

/** Puts the kept reads back, when they are this person's, this build's and recent. Call once the session is in. */
export function restorePersistedQueries(queryClient: QueryClient, session: SessionInfo | null | undefined): void {
  const stored = read();
  if (!stored) return;
  const owner = ownerOf(session);
  const fresh = Date.now() - stored.savedAt < MAX_AGE_MS;
  if (stored.version !== VERSION || stored.build !== __BUILD_ID__ || !owner || stored.owner !== owner || !fresh) {
    clearPersistedQueries();
    return;
  }
  try {
    hydrate(queryClient, stored.state);
  } catch {
    clearPersistedQueries();
  }
}

/**
 * Keeps the store up to date: a few seconds after the reads change, and when the tab is hidden or closed. Writes
 * only while someone is signed in; signed out, the store is removed.
 */
export function persistQueries(queryClient: QueryClient, sessionKey: readonly unknown[]): void {
  let timer: number | null = null;
  const save = () => {
    timer = null;
    const owner = ownerOf(queryClient.getQueryData<SessionInfo>(sessionKey));
    if (!owner) {
      clearPersistedQueries();
      return;
    }
    const state = dehydrate(queryClient, {
      shouldDehydrateQuery: (query) => query.state.status === "success" && persisted(query.queryKey),
    });
    const stored: Stored = { version: VERSION, build: __BUILD_ID__, owner, savedAt: Date.now(), state };
    try {
      const body = JSON.stringify(stored);
      if (body.length > MAX_BYTES) clearPersistedQueries();
      else window.localStorage.setItem(STORAGE_KEY, body);
    } catch {
      // Full or blocked: nothing kept this time.
    }
  };
  const soon = () => {
    if (timer === null) timer = window.setTimeout(save, 10_000);
  };
  queryClient.getQueryCache().subscribe((event) => {
    if ((event.type === "updated" || event.type === "removed") && (persisted(event.query.queryKey) || event.query.queryKey[0] === "session")) soon();
  });
  const now = () => {
    if (timer !== null) window.clearTimeout(timer);
    save();
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") now();
  });
  window.addEventListener("pagehide", now);
}
